import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { QueryStatus } from "@reduxjs/toolkit/dist/query"
import { Formik, FormikProps } from "formik"
import { LoanOfficer } from "../../../../types"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import SidePanel from "../../side-panel"
import bbApi from "../../../../api"
import { pushToast } from "../../../../redux/slices/notifications"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  useFetchBrandQuery,
  useFetchBrandsQuery,
  useLazyFetchLoanOfficersQuery,
  useUpdateLoanOfficerMutation
} from "../../../../redux/queries"
import { loFormSchema } from "./lo-form-schema"
import ManageLoForm from "./form-content"
import If from "../../if"
import { checkIsSuperAdmin } from "../../../utils"

const defaultValues: LoanOfficer = {
  name: "",
  first_name: "",
  last_name: "",
  uid: "",
  email: "",
  nmls: "",
  brand_nmls: "",
  app_link: "",
  nid: "",
  external: false,
  logo: "",
  disclaimer: "",
  picture: "",
  template_preference: {
    template_id: "",
    swatch_id: ""
  },
  bank_nid: "",
  bank: "",
  non_bank: false,
  position: "Loan Officer",
  roles: ["loan officer"],
  branch_nmls: "",
  pricing_status: "inactive",
  crm_type: "",
  crm_id: "",
  active: false,
  office_street_addr: "",
  office_zipcode: "",
  office_city: "",
  office_state: "",
  mobile_phone: "",
  office_phone: "",
  phone: "",
  brand_admin: false,
  masq_link: "",
  originator_id: "",
  biz_channel: "",
  about_me: "",
  about_bank: "",
  title: "",
  website_link: "",
  facebook_link: "",
  linkedin_link: "",
  special_disclosure_link: "",
  created: 0,
  org_id: -1
}

interface Props {
  data?: LoanOfficer
  brandId?: string
  open: boolean
  tabsVisible?: boolean
  onClose: () => void
  setFormRef?: (formRef: RefObject<FormikProps<LoanOfficer>>) => void
  isInSidePanel: boolean
}

const EditLoProfile: FC<Props> = ({
  data,
  brandId,
  open,
  tabsVisible = true,
  onClose,
  setFormRef,
  isInSidePanel
}) => {
  const dispatch = useAppDispatch()

  const user = useAppSelector(selectUserDetails)

  const [
    updateLoanOfficer,
    { data: response, isSuccess, isError, isLoading, reset }
  ] = useUpdateLoanOfficerMutation()

  const [lazyFetchLoanOfficers] = useLazyFetchLoanOfficersQuery()

  const { data: brandDetails } = useFetchBrandQuery(data?.bank_nid ?? "", {
    skip: !data?.bank_nid
  })

  const isSuperAdmin = checkIsSuperAdmin(user?.roles ?? [])

  const { data: brands } = useFetchBrandsQuery(
    isSuperAdmin
      ? { limit: 999 }
      : { org_id: brandDetails?.org_id || user?.org?.id },
    { skip: !isSuperAdmin && !brandDetails?.org_id && !user?.org?.id }
  )

  const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<File>()
  const [filename, setFilename] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const formRef = useRef<FormikProps<LoanOfficer>>(null)

  const isCreate = !(data?.uid || data?.loid)

  const setFormField = (name: string, val: string | number) => {
    formRef.current?.setFieldValue(name, val)
  }

  const validate = async (values: LoanOfficer) => {
    const errors: Partial<LoanOfficer> = {}

    if (values.nmls) {
      const { data, status } = await lazyFetchLoanOfficers({
        nmls: values.nmls
      })

      if (
        status === QueryStatus.fulfilled &&
        data?.los.length > 0 &&
        data?.los[0].loid !== values.uid &&
        data?.los[0].loid !== values.loid
      ) {
        errors.nmls = `This NMLS is already in use by ${data.los[0].name} (${data.los[0].bank})`
        return errors
      }
    }

    if (values.email) {
      const { data, status } = await lazyFetchLoanOfficers({
        email: values.email
      })

      if (
        status === QueryStatus.fulfilled &&
        data?.los.length &&
        data.los[0].uid !== values.uid &&
        data.los[0].uid !== values.loid
      ) {
        errors.email = `This email is already in use by ${data.los[0].name} (${data.los[0].bank})`
        return errors
      }
    }

    return errors
  }

  const resetForm = useCallback(() => {
    setLoading(false)
    setFile(undefined)
    setFilename("")
    formRef.current?.resetForm()
    reset()
  }, [reset])

  const uploadPicture = useCallback(async () => {
    try {
      if (file && filename) {
        setLoading(true)
        const data = await bbApi.asset.uploadImage(file, filename)

        if ("status" in data) {
          dispatch(
            pushToast({
              msg: "Failed to upload file, please try again later.",
              timeout: 3000
            })
          )
        } else {
          formRef.current?.setFieldValue("picture", data.url)
        }

        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
    }
  }, [dispatch, file, filename])

  const onSubmit = async () => {
    if (formRef.current?.values) {
      updateLoanOfficer(formRef.current.values)
    }
  }

  const submitFormHandler = () => {
    setIsSubmitting(true)
    formRef.current?.handleSubmit()
  }

  const handleClose = () => {
    formRef.current?.resetForm()
    onClose()
  }

  useEffect(() => {
    uploadPicture()
  }, [uploadPicture])

  useEffect(() => {
    if (formRef.current && setFormRef) {
      setFormRef(formRef)
    }
  }, [setFormRef])

  const handleSuccess = useCallback(() => {
    dispatch(
      pushToast({
        msg: `Successfully ${isCreate ? "created" : "updated"} loan officer`,
        theme: "success",
        timeout: 3000
      })
    )
    resetForm()
    onClose()
  }, [dispatch, isCreate, onClose, resetForm])

  const handleError = useCallback(() => {
    dispatch(
      pushToast({
        msg: `Failed to ${isCreate ? "create" : "update"} loan officer`,
        theme: "danger",
        timeout: 3000
      })
    )
    resetForm()
  }, [dispatch, isCreate, resetForm])

  useEffect(() => {
    if (isSuccess && response?.status === "success") {
      handleSuccess()
    } else if (isError || response?.status === "error") {
      handleError()
    }
    setIsSubmitting(false)
  }, [handleError, handleSuccess, isSuccess, isError, response?.status])

  const brand = useMemo(
    () => brands?.find(b => b.id === brandId),
    [brands, brandId]
  )

  const initialValues = data || {
    ...defaultValues,
    bank_nid: brandId ?? "",
    bank: brand?.name ?? ""
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loFormSchema}
      validate={validate}
      onSubmit={onSubmit}
      innerRef={formRef}
      validateOnBlur
      enableReinitialize
    >
      {({ values, dirty, isValid }) => (
        <If
          condition={isInSidePanel}
          then={
            <SidePanel
              title={
                isCreate
                  ? "Create Loan officer profile"
                  : "Edit Loan officer profile"
              }
              open={open}
              onClose={handleClose}
              isSubmitting={isSubmitting}
              actions={[
                {
                  label: isCreate ? "Submit" : "Save changes",
                  type: "primary",
                  onClick: submitFormHandler,
                  disabled: loading || isLoading || !dirty || !isValid
                }
              ]}
            >
              <ManageLoForm
                values={values}
                brands={brands ?? []}
                file={file}
                filename={filename}
                tabsVisible={tabsVisible}
                setFile={setFile}
                setFilename={setFilename}
                setFormField={setFormField}
              />
            </SidePanel>
          }
          else={
            <ManageLoForm
              values={values}
              brands={brands ?? []}
              file={file}
              filename={filename}
              tabsVisible={tabsVisible}
              setFile={setFile}
              setFilename={setFilename}
              setFormField={setFormField}
            />
          }
        />
      )}
    </Formik>
  )
}

export default EditLoProfile
